
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { isPasswordStrong } from "@/utils/passwordValidation";
import { validateEmail, validateName, validatePhone, validateCompany, authRateLimit } from "@/utils/inputValidation";

export const useRegister = (
  setIsLoading: (loading: boolean) => void,
  setAuthError: (error: string | null) => void
) => {
  // Register new user
  const register = async (
    email: string, 
    password: string, 
    fullName: string,
    firstName?: string,
    lastName?: string,
    company?: string,
    phone?: string,
    role?: string,
    invitationToken?: string
  ): Promise<boolean> => {
    setIsLoading(true);
    setAuthError(null);
    
    // Rate limiting check
    if (!authRateLimit.isAllowed(email)) {
      const remainingTime = Math.ceil(authRateLimit.getRemainingTime(email) / 1000);
      const errorMsg = `För många registreringsförsök. Försök igen om ${remainingTime} sekunder.`;
      setAuthError(errorMsg);
      toast.error("Registrering misslyckades", { description: errorMsg });
      setIsLoading(false);
      return false;
    }
    
    // Enhanced input validation
    const emailValidation = validateEmail(email);
    if (!emailValidation.isValid) {
      setAuthError(emailValidation.error || 'Ogiltig e-postadress');
      toast.error("Registrering misslyckades", { description: emailValidation.error });
      setIsLoading(false);
      return false;
    }
    
    if (!isPasswordStrong(password)) {
      setAuthError('Lösenordet uppfyller inte säkerhetskraven');
      toast.error("Registrering misslyckades", { description: 'Lösenordet uppfyller inte säkerhetskraven' });
      setIsLoading(false);
      return false;
    }
    
    const nameValidation = validateName(fullName, 'Fullständigt namn');
    if (!nameValidation.isValid) {
      setAuthError(nameValidation.error || 'Ogiltigt namn');
      toast.error("Registrering misslyckades", { description: nameValidation.error });
      setIsLoading(false);
      return false;
    }
    
    if (firstName) {
      const firstNameValidation = validateName(firstName, 'Förnamn');
      if (!firstNameValidation.isValid) {
        setAuthError(firstNameValidation.error || 'Ogiltigt förnamn');
        toast.error("Registrering misslyckades", { description: firstNameValidation.error });
        setIsLoading(false);
        return false;
      }
    }
    
    if (lastName) {
      const lastNameValidation = validateName(lastName, 'Efternamn');
      if (!lastNameValidation.isValid) {
        setAuthError(lastNameValidation.error || 'Ogiltigt efternamn');
        toast.error("Registrering misslyckades", { description: lastNameValidation.error });
        setIsLoading(false);
        return false;
      }
    }
    
    if (phone) {
      const phoneValidation = validatePhone(phone);
      if (!phoneValidation.isValid) {
        setAuthError(phoneValidation.error || 'Ogiltigt telefonnummer');
        toast.error("Registrering misslyckades", { description: phoneValidation.error });
        setIsLoading(false);
        return false;
      }
    }
    
    if (company) {
      const companyValidation = validateCompany(company);
      if (!companyValidation.isValid) {
        setAuthError(companyValidation.error || 'Ogiltigt företagsnamn');
        toast.error("Registrering misslyckades", { description: companyValidation.error });
        setIsLoading(false);
        return false;
      }
    }
    
    // Validate invitation token
    if (!invitationToken) {
      setAuthError("Inbjudningstoken krävs för registrering");
      toast.error("Registrering misslyckades", {
        description: "Inbjudningstoken krävs för registrering"
      });
      setIsLoading(false);
      return false;
    }
    
    try {
      // Get the URL to redirect to after email confirmation
      const redirectTo = `${window.location.origin}/auth`;
      
      console.log("Starting registration process for:", email);
      
      // Call Supabase auth.signUp
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: redirectTo,
          data: {
            full_name: fullName,
            first_name: firstName || "",
            last_name: lastName || "",
            company: company || "",
            phone: phone || "",
            role: role || "",
            invitation_token: invitationToken
          },
        }
      });
      
      if (error) {
        console.error("Registration error:", error);
        
        // Check for specific error types to provide better feedback
        if (error.message.includes("User already registered")) {
          setAuthError("E-postadressen är redan registrerad. Försök att logga in istället.");
          toast.error("Registrering misslyckades", {
            description: "E-postadressen är redan registrerad. Försök att logga in istället.",
          });
        } else if (error.status === 422) {
          setAuthError("Ogiltig e-postadress eller lösenord. Kontrollera dina uppgifter.");
          toast.error("Registrering misslyckades", {
            description: "Ogiltig e-postadress eller lösenord. Kontrollera dina uppgifter.",
          });
        } else {
          setAuthError(error.message);
          toast.error("Registrering misslyckades", {
            description: error.message,
          });
        }
        return false;
      }
      
      // Check if the user was created
      if (data && data.user) {
        console.log("User created successfully:", data.user.id);
        
        // Mark invitation as used after successful registration
        if (invitationToken) {
          try {
            const { error: markError } = await supabase.rpc('mark_invitation_used', {
              invitation_token: invitationToken,
              user_id: data.user.id
            });
            
            if (markError) {
              console.error("Error marking invitation as used:", markError);
            }
          } catch (err) {
            console.error("Error calling mark_invitation_used:", err);
          }
        }
        
        toast.success("Konto skapat!", {
          description: "Kontrollera din e-post för att bekräfta ditt konto.",
        });
        return true;
      } else {
        console.error("User data is missing after registration");
        setAuthError("Något gick fel vid registrering.");
        return false;
      }
    } catch (error: any) {
      console.error("Unexpected registration error:", error);
      setAuthError(error.message || "Ett oväntat fel inträffade");
      toast.error("Registrering misslyckades", {
        description: error.message || "Ett oväntat fel inträffade",
      });
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  return { register };
};
